import React, { useState } from 'react';

import { Typography, IconButton, InputAdornment } from '@mui/material';

import LockResetIcon from '@mui/icons-material/LockReset';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import { FormContainer, Form, InputField, SubmitButton } from './styles';
import api from '../../services/api';
import { useToast } from '../../hooks/toast';

interface FormData {
  email: string;
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

interface ValidationErrors {
  newPassword?: string;
  confirmNewPassword?: string;
}

const PasswordRecovery: React.FC = () => {
  const { addToast } = useToast();
  const [formData, setFormData] = useState<FormData>({
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });

  const [errors, setErrors] = useState<ValidationErrors>({});

  // States to toggle password visibility
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  const validatePassword = (password: string): string | null => {
    if (password.length < 12) {
      return 'Password must be at least 12 characters long';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return 'Password must contain at least one special character';
    }
    return null;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Clear errors when user starts typing
    if (name === 'newPassword' || name === 'confirmNewPassword') {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();

    const newErrors: ValidationErrors = {};

    // Validate new password
    const passwordError = validatePassword(formData.newPassword);
    if (passwordError) {
      newErrors.newPassword = passwordError;
    }

    // Validate password confirmation
    if (formData.newPassword !== formData.confirmNewPassword) {
      newErrors.confirmNewPassword = 'Passwords do not match';
    }

    if (
      formData.newPassword === formData.confirmNewPassword &&
      formData.newPassword === formData.currentPassword
    ) {
      newErrors.confirmNewPassword =
        'New password cannot be the same as current password';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    api
      .put('/users/changeMyPassword', formData)
      .then(response => {
        if (response.data?.changed === true) {
          addToast({
            type: 'success',
            title: 'Success',
            description: 'Password changed successfully',
          });

          setFormData({
            email: '',
            currentPassword: '',
            newPassword: '',
            confirmNewPassword: '',
          });
        } else {
          addToast({
            type: 'error',
            title: 'Error',
            description: response.data?.msg || 'Unknown error occurred',
          });
        }
      })
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Error',
          description:
            'An error occurred while changing the password. Check the email address and try again.',
        });
      });
  };

  return (
    <FormContainer maxWidth="sm">
      <Form onSubmit={handleSubmit}>
        <h1 style={{ textAlign: 'center' }}>
          <LockResetIcon />
        </h1>
        <Typography variant="h4" align="center" gutterBottom>
          Password Reset
        </Typography>
        <div
          id="passwordRules"
          style={{ marginBottom: '20px', marginLeft: '10%' }}
        >
          <p>* Password must be at least 12 characters long</p>
          <p>* Password must contain at least one uppercase letter</p>
          <p>* Password must contain at least one lowercase letter</p>
          <p>* Password must contain at least one special character</p>
        </div>

        <InputField
          fullWidth
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          required
        />

        <InputField
          fullWidth
          label="Current Password"
          name="currentPassword"
          type={showCurrentPassword ? 'text' : 'password'}
          value={formData.currentPassword}
          onChange={handleChange}
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowCurrentPassword(p => !p)}>
                  {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <InputField
          fullWidth
          label="New Password"
          name="newPassword"
          type={showNewPassword ? 'text' : 'password'}
          value={formData.newPassword}
          onChange={handleChange}
          required
          error={!!errors.newPassword}
          helperText={errors.newPassword}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowNewPassword(p => !p)}>
                  {showNewPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <InputField
          fullWidth
          label="Confirm New Password"
          name="confirmNewPassword"
          type={showConfirmNewPassword ? 'text' : 'password'}
          value={formData.confirmNewPassword}
          onChange={handleChange}
          required
          error={!!errors.confirmNewPassword}
          helperText={errors.confirmNewPassword}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowConfirmNewPassword(p => !p)}>
                  {showConfirmNewPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <SubmitButton
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
        >
          Submit
        </SubmitButton>
      </Form>
    </FormContainer>
  );
};

export default PasswordRecovery;
